// src/components/UniversityAnalytics.jsx
import React, { useEffect, useMemo, useRef } from 'react';
import { Chart } from 'chart.js/auto';

function useChart(canvasRef, deps, buildConfigFn) {
  const chartRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const config = buildConfigFn();
    if (!config) return;

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvas, config);

    return () => chartRef.current?.destroy();
    // eslint-disable-next-line
  }, deps);
}

const formatName = (n) => (n ? String(n).trim() : 'Tidak diketahui');

export default function UniversityAnalytics({
  universities = [],
  loading = false,
  showTable = false,
  showInsight = false,
}) {
  const top10Ref = useRef(null);
  const tierRef = useRef(null);

  // ─────────────────────────────────────────────
  // 🔍 NORMALIZE + SORT
  // ─────────────────────────────────────────────
  const data = useMemo(() => {
    if (!Array.isArray(universities)) return [];
    return [...universities]
      .map((u) => ({
        university_name: u.university_name ?? null,
        total_students: Number(u.total_students ?? 0),
      }))
      .sort((a, b) => b.total_students - a.total_students);
  }, [universities]);

  const totalStudents = useMemo(
    () => data.reduce((s, u) => s + u.total_students, 0),
    [data]
  );

  const top10 = data.slice(0, 10);

  // ─────────────────────────────────────────────
  // 📊 Tier Grouping
  // ─────────────────────────────────────────────
  const tiers = useMemo(() => {
    const t1 = data.filter((u) => u.total_students > 100).length;
    const t2 = data.filter(
      (u) => u.total_students >= 50 && u.total_students <= 100
    ).length;
    const t3 = data.filter(
      (u) => u.total_students >= 20 && u.total_students <= 49
    ).length;
    const t4 = data.filter(
      (u) => u.total_students >= 1 && u.total_students <= 19
    ).length;
    return { t1, t2, t3, t4 };
  }, [data]);

  // ─────────────────────────────────────────────
  // 📊 Pareto (80/20)
  // ─────────────────────────────────────────────
  const pareto = useMemo(() => {
    if (!totalStudents) return { count: 0, percent: 0, topSum: 0 };

    let running = 0;
    let count = 0;

    for (const u of data) {
      running += u.total_students;
      count++;
      if (running >= totalStudents * 0.8) break;
    }

    return {
      count,
      percent: ((running / totalStudents) * 100).toFixed(1),
      topSum: running,
    };
  }, [data, totalStudents]);

  const top5Sum = data.slice(0, 5).reduce((s, u) => s + u.total_students, 0);
  const top5Pct = totalStudents
    ? ((top5Sum / totalStudents) * 100).toFixed(1)
    : 0;

  // Pre-calc values for insights (must be before early return)
  const top5 = useMemo(() => data.slice(0, 5), [data]);

  const top5Contribution = useMemo(() => {
    if (totalStudents === 0) return 0;
    const top5Total = top5.reduce(
      (sum, item) => sum + (item.total_students || 0),
      0
    );
    return ((top5Total / totalStudents) * 100).toFixed(1);
  }, [top5, totalStudents]);

  const topUniversity = data[0] || null;

  // Outliers: kampus besar yg punya jumlah mahasiswa kecil (misal < 10)
  const outliers = useMemo(
    () => data.filter((u) => (u.total_students || 0) < 10 && u.university_name),
    [data]
  );

  // Null universities
  const nullEntries = useMemo(
    () => data.filter((u) => !u.university_name),
    [data]
  );

  // Wilayah: cluster berbasis nama universitas (sederhana)
  const cluster = useMemo(
    () => ({
      sumatera: data.filter((u) =>
        /lampung|sumatera|riau|padang|andalas|palembang|hasanuddin/i.test(
          u.university_name || ''
        )
      ),
      jawaBarat: data.filter((u) =>
        /bandung|padjadjaran|bogor|jakarta|depok|tirtayasa/i.test(
          u.university_name || ''
        )
      ),
      jawaTimur: data.filter((u) =>
        /surabaya|airlangga|brawijaya|jember|malang|ub/i.test(
          u.university_name || ''
        )
      ),
    }),
    [data]
  );

  // ─────────────────────────────────────────────
  // 📈 RENDER CHART → Top 10
  // ─────────────────────────────────────────────
  useChart(top10Ref, [top10], () => ({
    type: 'bar',
    data: {
      labels: top10.map((u) => formatName(u.university_name)),
      datasets: [
        {
          label: 'Total Mahasiswa',
          data: top10.map((u) => u.total_students),
          backgroundColor: 'rgba(13,110,253,0.6)',
          borderRadius: 6,
        },
      ],
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { beginAtZero: true },
        y: { ticks: { autoSkip: false } },
      },
    },
  }));

  // ─────────────────────────────────────────────
  // 📈 Tier Doughnut Chart
  // ─────────────────────────────────────────────
  useChart(tierRef, [tiers.t1, tiers.t2, tiers.t3, tiers.t4], () => ({
    type: 'doughnut',
    data: {
      labels: [
        'Tier 1 (>100)',
        'Tier 2 (50–100)',
        'Tier 3 (20–49)',
        'Tier 4 (1–19)',
      ],
      datasets: [
        {
          data: [tiers.t1, tiers.t2, tiers.t3, tiers.t4],
          backgroundColor: ['#0d6efd', '#198754', '#ffc107', '#6c757d'],
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
      },
    },
  }));

  // Early return after all hooks - only for empty data (not loading)
  if (!loading && (!universities || universities.length === 0)) {
    return (
      <div className="text-muted small text-center py-4">
        <i className="bi bi-inbox fs-3 d-block mb-2 opacity-50"></i>
        Tidak ada data universitas untuk ditampilkan.
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // 🎨 UI Rendering
  // ─────────────────────────────────────────────
  return (
    <div>
      {/* SUMMARY CARDS */}
      <div className="row g-2 mb-3 small">
        <div className="col-md-4 col-6">
          <div className="card shadow-sm rounded-4 border-0 p-2 h-100">
            <div className="small text-muted">Total Mahasiswa</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">{totalStudents}</div>
                <div className="small text-muted">
                  Total kampus: {data.length}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">Top 5 Contribution</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">{top5Pct}%</div>
                <div className="small text-muted">({top5Sum} mahasiswa)</div>
              </>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">Pareto (80%)</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">{pareto.count} kampus</div>
                <div className="small text-muted">
                  {pareto.percent}% dari total
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CHART + RIGHT SIDEBAR */}
      <div className="row mb-4">
        {/* LEFT TOP10 CHART */}
        <div className="col-lg-7 mb-3">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-2">
                <i className="bi bi-bar-chart-line me-2 text-primary"></i>
                Top 10 Universitas
              </h6>
              {loading ? (
                <div className="text-center py-5" style={{ height: 360 }}>
                  <div className="spinner-border text-primary" />
                  <p className="text-muted mt-3 mb-0">Memuat grafik...</p>
                </div>
              ) : (
                <>
                  <div style={{ height: 360 }}>
                    <canvas ref={top10Ref} />
                  </div>
                  <p className="chart-caption">
                    <i className="bi bi-lightbulb text-warning me-1"></i>
                    Cara baca: makin panjang batang, makin banyak mahasiswa dari kampus itu. Urutan sudah dari yang terbesar.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT TIER + TABLE */}
        <div className="col-lg-5">
          {/* DOUGHNUT */}
          <div className="card shadow-sm rounded-4 border-0 mb-3">
            <div className="card-body">
              <h6 className="fw-semibold mb-2">
                <i className="bi bi-pie-chart me-2 text-success"></i>
                Distribusi Tier
              </h6>
              {loading ? (
                <div className="text-center py-5" style={{ height: 200 }}>
                  <div className="spinner-border text-success" />
                  <p className="text-muted mt-3 mb-0 small">
                    Memuat distribusi...
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ height: 200 }}>
                    <canvas ref={tierRef} />
                  </div>
                  <p className="chart-caption">
                    <i className="bi bi-lightbulb text-warning me-1"></i>
                    Cara baca: kampus dikelompokkan dari kontribusi terbesar (T1) ke terkecil (T4) berdasarkan jumlah mahasiswa.
                  </p>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* FULL TABLE */}
      {showTable && (
        <div className="card shadow-sm rounded-4 border-0 mb-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-table me-2 text-primary"></i>
              Semua Universitas
            </h6>

            <div className="table-responsive" style={{ maxHeight: 420 }}>
              <table className="table table-striped table-hover small">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>Universitas</th>
                    <th style={{ width: 120 }}>Mahasiswa</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((u, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td
                        style={{
                          maxWidth: 360,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {formatName(u.university_name)}
                      </td>
                      <td>{u.total_students}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showInsight && (
        <div className="card border-0 shadow-sm rounded-4 mb-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-lightbulb-fill text-warning me-2"></i>
              Insight Universitas
            </h6>

            <ul className="small text-muted mb-0">
              {/* TOP UNIVERSITY */}
              <li className="mb-2">
                <strong>{topUniversity?.university_name}</strong> menjadi
                penyumbang mahasiswa terbesar dengan{' '}
                <strong>{topUniversity?.total_students}</strong> peserta.
              </li>

              {/* TOP 5 Contribution */}
              <li className="mb-2">
                Lima universitas teratas berkontribusi sebesar{' '}
                <strong>{top5Contribution}%</strong> dari total{' '}
                <strong>{totalStudents}</strong> mahasiswa CLP.
              </li>

              {/* Regional insight */}
              <li className="mb-2">
                Rekrutmen paling kuat berasal dari wilayah{' '}
                <strong>Sumatera</strong> ({cluster.sumatera.length}{' '}
                universitas) dan <strong>Jawa Barat</strong> (
                {cluster.jawaBarat.length} universitas). Sementara wilayah{' '}
                <strong>Jawa Timur</strong> masih relatif kecil (
                {cluster.jawaTimur.length} kampus).
              </li>

              {/* Outliers */}
              {outliers.length > 0 && (
                <li className="mb-2">
                  Beberapa kampus besar memiliki kontribusi kecil (&lt;10
                  mahasiswa), misalnya:{' '}
                  {outliers.slice(0, 5).map((o, i) => (
                    <span key={i}>
                      <strong>{o.university_name}</strong> ({o.total_students})
                      {i < outliers.length - 1 && ', '}
                    </span>
                  ))}
                  . Ini bisa menjadi peluang penguatan kerjasama kampus.
                </li>
              )}

              {/* Null / Missing */}
              {nullEntries.length > 0 && (
                <li className="mb-2">
                  Terdapat <strong>{nullEntries.length}</strong> mahasiswa tanpa
                  data universitas (<em>null</em>). Perlu perbaikan data input
                  untuk menjaga kualitas analitik.
                </li>
              )}

              {/* Total Summary */}
              <li className="mb-1">
                Total kampus tercatat: <strong>{universities.length}</strong>.
              </li>
              <li className="mb-1">
                Total mahasiswa keseluruhan: <strong>{totalStudents}</strong>.
              </li>
            </ul>
          </div>
        </div>
      )}

      <style>{`
        .chart-caption { font-size: 0.75rem; color: #6c757d; margin: 10px 0 0; }
      `}</style>
    </div>
  );
}
